import React, { useEffect, useState, useMemo } from "react";
import { Bell, AlertCircle, Mail, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { H1, Sub, Skeleton } from "./_shared";

// Central sport colour map — each sport gets its own stack colour so the
// bars read as "who wants what" at a glance. Keep in sync with the UI
// palette; unknown sports fall back to neutral grey.
const SPORT_COLORS = {
  padel: "#00E676",
  pickleball: "#007AFF",
  tennis: "#FF9F0A",
  golf: "#FFD60A",
  basketball: "#FF453A",
  football: "#BF5AF2",
  volleyball: "#64D2FF",
  all: "#a1a1aa",
};

function sportColor(slug) {
  return SPORT_COLORS[slug] || "#52525b";
}

export default function WatchersSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/watch/stats").then((r) => setData(r.data)).catch(() => setData({ error: true })); }, []);

  // Derive chart dimensions from the data. Bars are rendered inline SVG so
  // the page stays dependency-free (no recharts, d3, etc).
  const chart = useMemo(() => {
    if (!data?.by_city?.length) return null;
    const W = 760, H = 220, padL = 40, padB = 56, padT = 8, padR = 8;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;
    const maxTotal = Math.max(...data.by_city.map((c) => c.total), 1);
    const barGap = 4;
    const barW = Math.max(8, plotW / data.by_city.length - barGap);
    const yTick = Math.max(1, Math.ceil(maxTotal / 4));
    const yLines = [];
    for (let v = 0; v <= maxTotal; v += yTick) {
      yLines.push({ v, y: padT + plotH - (v / maxTotal) * plotH });
    }
    return { W, H, padL, padB, padT, padR, plotW, plotH, maxTotal, barW, barGap, yLines };
  }, [data]);

  const trendChart = useMemo(() => {
    if (!data?.trend_30d?.length) return null;
    const W = 760, H = 100, padL = 24, padB = 20, padT = 8, padR = 8;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    const maxCount = Math.max(...data.trend_30d.map((d) => d.count), 1);
    const step = plotW / Math.max(1, data.trend_30d.length - 1);
    const pts = data.trend_30d.map((d, i) => ({
      x: padL + i * step,
      y: padT + plotH - (d.count / maxCount) * plotH,
      ...d,
    }));
    const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
    const area = path + ` L ${pts[pts.length - 1].x.toFixed(1)} ${padT + plotH} L ${padL} ${padT + plotH} Z`;
    return { W, H, padT, padL, padB, padR, plotH, plotW, maxCount, path, area, pts };
  }, [data]);

  if (!data) return <Skeleton />;
  if (data.error) return <div className="pp-card p-10 text-center text-[#FF3B30]" data-testid="cp-watchers-error">Couldn't load watcher analytics.</div>;

  const totals = data.totals || {};
  const sportsInChart = Array.from(new Set((data.by_city || []).flatMap((c) => Object.keys(c.by_sport || {}))));

  return (
    <div data-testid="cp-watchers">
      <H1>Watchers</H1>
      <Sub>Every anonymous visitor who asked to be notified when a new venue opens in their city. Tap a row to see the public landing page.</Sub>

      {/* KPI tiles */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cp-watchers-kpis">
        <Kpi label="Active watchers" value={totals.active ?? 0} icon={Bell} tint="#00E676" testid="kpi-active" />
        <Kpi label="Unsubscribed" value={totals.inactive ?? 0} icon={Mail} tint="#a1a1aa" testid="kpi-inactive" />
        <Kpi label="Unique cities" value={totals.unique_cities ?? 0} icon={TrendingUp} tint="#007AFF" testid="kpi-cities" />
        <Kpi label="Demand signals" value={(data.demand_signals || []).length} icon={AlertCircle} tint="#FFD60A" testid="kpi-signals" />
      </div>

      {/* Demand signals — highest-ROI output. Shows cities where people are
          asking but we have zero verified venues. */}
      {data.demand_signals?.length > 0 && (
        <section className="mt-8" data-testid="cp-watchers-signals">
          <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
            <AlertCircle size={16} className="text-[#FFD60A]" /> Demand signals
          </h3>
          <div className="pp-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-white/5"><tr>
                <th className="text-start px-4 py-2">City</th>
                <th className="text-start px-4 py-2">Sport</th>
                <th className="text-start px-4 py-2">Watchers</th>
                <th className="text-start px-4 py-2">Verified venues</th>
                <th className="text-end px-4 py-2">Action</th>
              </tr></thead>
              <tbody>
                {data.demand_signals.map((s) => (
                  <tr key={`${s.city_slug}-${s.sport || "all"}`} className="border-t border-white/5" data-testid={`cp-watchers-signal-${s.city_slug}-${s.sport || "all"}`}>
                    <td className="px-4 py-2 font-medium">{s.city_display}</td>
                    <td className="px-4 py-2"><span className="pp-chip" style={{ borderColor: `${sportColor(s.sport || "all")}55`, color: sportColor(s.sport || "all") }}>{s.sport || "all sports"}</span></td>
                    <td className="px-4 py-2 font-mono font-bold text-[#FFD60A]">{s.watchers}</td>
                    <td className="px-4 py-2 text-[#FF3B30]">0</td>
                    <td className="px-4 py-2 text-end">
                      <Link to={`/clubs/in/${s.city_slug}${s.sport ? `/${s.sport}` : ""}`} className="text-xs text-[#00E676] hover:underline">Open landing →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Top cities stacked bar chart */}
      {chart && data.by_city?.length > 0 && (
        <section className="mt-8" data-testid="cp-watchers-by-city">
          <h3 className="font-heading text-lg font-bold mb-3">Top cities by active watchers</h3>
          <div className="pp-card p-4 overflow-x-auto">
            <svg viewBox={`0 0 ${chart.W} ${chart.H}`} className="w-full min-w-[600px]" role="img" aria-label="Watchers per city (stacked by sport)">
              {/* Y axis gridlines + labels */}
              {chart.yLines.map((g) => (
                <g key={g.v}>
                  <line x1={chart.padL} x2={chart.W - chart.padR} y1={g.y} y2={g.y} stroke="rgba(255,255,255,0.06)" strokeDasharray="2 4" />
                  <text x={chart.padL - 6} y={g.y + 3} textAnchor="end" fontSize="9" fill="#71717a">{g.v}</text>
                </g>
              ))}
              {/* Bars */}
              {data.by_city.map((c, i) => {
                const x = chart.padL + i * (chart.barW + chart.barGap);
                let cumulative = 0;
                return (
                  <g key={c.city_slug} data-testid={`cp-watchers-city-bar-${c.city_slug}`}>
                    {Object.entries(c.by_sport).map(([sport, n]) => {
                      const h = (n / chart.maxTotal) * chart.plotH;
                      const y = chart.padT + chart.plotH - cumulative - h;
                      cumulative += h;
                      return (
                        <rect
                          key={sport}
                          x={x}
                          y={y}
                          width={chart.barW}
                          height={h}
                          fill={sportColor(sport)}
                          opacity="0.85"
                          rx="2"
                        >
                          <title>{`${c.city_display} · ${sport}: ${n}`}</title>
                        </rect>
                      );
                    })}
                    {/* City label — rotated 45° so it fits */}
                    <text
                      x={x + chart.barW / 2}
                      y={chart.H - chart.padB + 14}
                      fontSize="10"
                      fill="#a1a1aa"
                      textAnchor="end"
                      transform={`rotate(-35 ${x + chart.barW / 2} ${chart.H - chart.padB + 14})`}
                    >
                      {c.city_display.length > 14 ? c.city_display.slice(0, 13) + "…" : c.city_display}
                    </text>
                    {/* Total on top */}
                    <text
                      x={x + chart.barW / 2}
                      y={chart.padT + chart.plotH - (c.total / chart.maxTotal) * chart.plotH - 4}
                      fontSize="10"
                      fontWeight="600"
                      fill="#E6E6E6"
                      textAnchor="middle"
                    >
                      {c.total}
                    </text>
                  </g>
                );
              })}
            </svg>
            {/* Sport legend */}
            <div className="mt-3 flex flex-wrap gap-3 text-[11px]" data-testid="cp-watchers-legend">
              {sportsInChart.map((s) => (
                <div key={s} className="flex items-center gap-1.5">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: sportColor(s) }} />
                  <span className="text-zinc-400">{s === "all" ? "all sports" : s}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 30-day trend sparkline */}
      {trendChart && (
        <section className="mt-8" data-testid="cp-watchers-trend">
          <h3 className="font-heading text-lg font-bold mb-3">New signups · last 30 days</h3>
          <div className="pp-card p-4 overflow-x-auto">
            <svg viewBox={`0 0 ${trendChart.W} ${trendChart.H}`} className="w-full min-w-[500px]" role="img" aria-label="New city watchers per day, last 30 days">
              <path d={trendChart.area} fill="#00E676" opacity="0.15" />
              <path d={trendChart.path} fill="none" stroke="#00E676" strokeWidth="2" />
              {trendChart.pts.map((p) => (
                p.count > 0 ? <circle key={p.day} cx={p.x} cy={p.y} r="2.5" fill="#00E676"><title>{`${p.day}: ${p.count}`}</title></circle> : null
              ))}
              {/* Y max label */}
              <text x={4} y={trendChart.padT + 6} fontSize="9" fill="#71717a">{trendChart.maxCount}</text>
              {/* X axis — first/last day */}
              <text x={trendChart.padL} y={trendChart.H - 4} fontSize="9" fill="#71717a">{trendChart.pts[0]?.day.slice(5)}</text>
              <text x={trendChart.W - trendChart.padR} y={trendChart.H - 4} fontSize="9" fill="#71717a" textAnchor="end">{trendChart.pts[trendChart.pts.length - 1]?.day.slice(5)}</text>
            </svg>
          </div>
        </section>
      )}

      {totals.active === 0 && (
        <div className="mt-8 pp-card p-10 text-center text-zinc-500" data-testid="cp-watchers-empty">
          No active watchers yet. Drop the link to a city landing page in socials — e.g. {"/clubs/in/madrid/padel"} — and folks will start subscribing.
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tint, testid }) {
  return (
    <div className="pp-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500">
        <Icon size={11} style={{ color: tint }} /> {label}
      </div>
      <div className="font-heading text-2xl font-black mt-1" style={{ color: tint }}>{value}</div>
    </div>
  );
}
